/**
 * Central registry for the Home "booking queue" widget presets. Each preset is
 * an actionable, pre-filtered slice of `GET /v1/bookings` surfaced as a compact
 * tile (needs-booking, rolled/delayed, and approaching-milestone lists).
 *
 * Filters are resolved at call time so the "today → today + N" date windows are
 * always relative to the current day. The same resolved filters power both the
 * in-tile fetch (`useBookingQueue`) and the tile's "View all" deep link
 * (`buildBookingsFilterUrl`).
 */
import { addDaysIso, todayIso } from "@/lib/dateHorizon";
import type { BookingsFilters } from "@/types/api";
import type { BookingQueuePreset } from "@/types/home";

/** How each preset's urgency badge is derived on a row. */
export type QueueHighlight = "etd" | "eta" | "cutoff" | "stuffing" | "tags";

export interface BookingQueuePresetDef {
  preset: BookingQueuePreset;
  /** Palette label + default tile title. */
  label: string;
  /** Palette description. */
  description: string;
  /** Default forward-looking window in days (milestone presets only). */
  horizonDays?: number;
  /** How the row urgency badge is computed. */
  highlight: QueueHighlight;
  /** Empty-state copy when no bookings match. */
  emptyMessage: string;
  /** Resolve the API filters for a given horizon (defaults to `horizonDays`). */
  resolveFilters: (horizonDays?: number) => BookingsFilters;
}

/** Non-terminal booking states — the "open bookings" convention shared with Kai. */
const EXCLUDE_TERMINAL = "delivered,cancelled,rebooked";

export const BOOKING_QUEUE_PRESETS: Record<
  BookingQueuePreset,
  BookingQueuePresetDef
> = {
  pending_book: {
    preset: "pending_book",
    label: "Needs booking",
    description: "Bookings awaiting an operations booking action.",
    highlight: "etd",
    emptyMessage: "Nothing waiting to be booked.",
    resolveFilters: () => ({
      state: "pending_book",
      sort_by: "departure_date",
      sort_dir: "asc",
    }),
  },
  rolled_delayed: {
    preset: "rolled_delayed",
    label: "Rolled / delayed",
    description: "Disrupted bookings that need urgent attention.",
    highlight: "tags",
    emptyMessage: "No rolled or delayed bookings.",
    resolveFilters: () => ({
      tag: "ROLLED,DELAYED",
      state_not: EXCLUDE_TERMINAL,
      sort_by: "departure_date",
      sort_dir: "asc",
    }),
  },
  cutoff_soon: {
    preset: "cutoff_soon",
    label: "Cutoffs approaching",
    description: "Bookings whose cargo cutoff is near.",
    horizonDays: 2,
    highlight: "cutoff",
    emptyMessage: "No cutoffs in this window.",
    resolveFilters: (horizonDays = 2) => ({
      cutoff_from: todayIso(),
      cutoff_to: addDaysIso(horizonDays),
      state_not: EXCLUDE_TERMINAL,
      sort_by: "effective_cutoff",
      sort_dir: "asc",
    }),
  },
  stuffing_soon: {
    preset: "stuffing_soon",
    label: "Stuffing approaching",
    description: "Bookings with stuffing dates coming up.",
    horizonDays: 2,
    highlight: "stuffing",
    emptyMessage: "No stuffing dates in this window.",
    resolveFilters: (horizonDays = 2) => ({
      stuffing_from: todayIso(),
      stuffing_to: addDaysIso(horizonDays),
      state_not: EXCLUDE_TERMINAL,
      sort_by: "stuffing_date",
      sort_dir: "asc",
    }),
  },
  departure_soon: {
    preset: "departure_soon",
    label: "Departures approaching",
    description: "Bookings departing within the next week.",
    horizonDays: 7,
    highlight: "etd",
    emptyMessage: "No departures in this window.",
    resolveFilters: (horizonDays = 7) => ({
      departure_from: todayIso(),
      departure_to: addDaysIso(horizonDays),
      state_not: EXCLUDE_TERMINAL,
      sort_by: "departure_date",
      sort_dir: "asc",
    }),
  },
  arrival_soon: {
    preset: "arrival_soon",
    label: "Arrivals approaching",
    description: "Bookings arriving within the next two weeks.",
    horizonDays: 14,
    highlight: "eta",
    emptyMessage: "No arrivals in this window.",
    resolveFilters: (horizonDays = 14) => ({
      eta_from: todayIso(),
      eta_to: addDaysIso(horizonDays),
      state_not: EXCLUDE_TERMINAL,
      sort_by: "arrival_date",
      sort_dir: "asc",
    }),
  },
};

export const BOOKING_QUEUE_PRESET_LIST: BookingQueuePresetDef[] =
  Object.values(BOOKING_QUEUE_PRESETS);

/** Resolve a preset definition, falling back to `pending_book` if unknown. */
export function bookingQueuePresetDef(
  preset: string | undefined,
): BookingQueuePresetDef {
  return (
    BOOKING_QUEUE_PRESETS[preset as BookingQueuePreset] ??
    BOOKING_QUEUE_PRESETS.pending_book
  );
}
